import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import ReferenceSelectorPanel, { useGenerateContext } from './components/ReferenceSelectorPanel';
import GenerationPanel from './components/GenerationPanel';

export default function ThemeGeneratePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, referenceFiles, loading, error } = useGenerateContext();

  const [selectedRefId, setSelectedRefId] = useState<number | null>(null);
  const [userIdcommerce, setUserIdcommerce] = useState<string>('');

  const userIdInt = user?.id ? (() => { const p = parseInt(String(user.id), 10); return Number.isNaN(p) ? 1 : p; })() : 1;

  useEffect(() => {
    if (selectedRefId == null && referenceFiles.length > 0) {
      setSelectedRefId(referenceFiles[0].id);
    }
  }, [referenceFiles, selectedRefId]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('users')
      .select('idcommerce')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setUserIdcommerce(data && data.idcommerce != null ? String(data.idcommerce) : String(user.id));
      })
      .catch(() => setUserIdcommerce(String(user.id)));
  }, [user]);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <button
            onClick={() => navigate('/superadmin/themes')}
            className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer mb-2"
          >
            <i className="ri-arrow-left-line"></i>
            Retour aux thèmes
          </button>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Génération de thèmes IA</h2>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Sélectionne un <strong className="text-foreground-700">fichier de référence</strong>, la <strong className="text-foreground-700">catégorie d&apos;activité</strong> et le <strong className="text-foreground-700">type de thème</strong> — l&apos;IA génère un thème complet et enregistré.
          </p>
        </div>
        <button
          onClick={() => navigate('/superadmin/themes/references')}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-200/70 hover:text-foreground-900 transition-colors cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Gérer les fichiers de référence
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
          <span className="text-xs text-red-700">{error}</span>
        </div>
      )}

      {/* Deux colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <ReferenceSelectorPanel
            referenceFiles={referenceFiles}
            selectedId={selectedRefId}
            onSelect={setSelectedRefId}
            loading={loading}
            error={error}
          />
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <GenerationPanel
            referenceFiles={referenceFiles}
            selectedRefId={selectedRefId}
            categories={categories}
            userId={userIdInt}
            userIdcommerce={userIdcommerce}
          />
        </div>
      </div>
    </div>
  );
}